import { useState, useCallback } from "react";

export const STEP_LABELS = [
  "Welcome",
  "Identity",
  "DOT Lookup",
  "Vehicle",
  "Questions",
  "Quote",
  "Photos",
  "MVR",
  "Payment",
  "Confirmation",
] as const;

export type StepLabel = (typeof STEP_LABELS)[number];

export interface CarrierData {
  legalName: string;
  dba: string;
  usdot: string;
  mc: string;
  operationType: string;
  carrierType: string;
  powerUnits: number;
  authorityStatus: "Active" | "Inactive";
}

export interface VehicleData {
  vin: string;
  year: string;
  make: string;
  model: string;
  bodyType: string;
  gvwr: string;
  acv: string;
}

export interface TrailerData {
  vin: string;
  year: string;
  make: string;
  model: string;
  trailerType: string;
  acv: string;
  photos: string[];
}

export interface TeamDriverData {
  firstName: string;
  lastName: string;
  dob: string;
  licenseNumber: string;
  issuingState: string;
  licenseClass: string;
  expirationDate: string;
  cmvExperience: string;
}

export interface UWAnswers {
  driverStatus: string;
  carrierName: string;
  operationScope: string;
  garagingZip: string;
  priorInsurance: string;
  losses: string;
  lossCount: string;
  cmvExperience: string;
  ticketsViolations: string;
}

export interface QuoteData {
  monthlyPremium: number;
  baseALPremium: number;
  stateFees: number;
  ntlPrice: number;
  ocacPrice: number;
  ntlIncluded: boolean;
  ocacAdded: boolean;
  ntlDeclined: boolean;
  ocacDeclined: boolean;
  trailers: TrailerData[];
  teamDrivers: TeamDriverData[];
}

export interface MVRResult {
  status: "proceed" | "reprice" | "block";
  newPremium?: number;
  reason?: string;
}

export interface PortalState {
  currentStep: number;
  consents: {
    tcpa: boolean;
    esign: boolean;
    privacy: boolean;
    cameraLocation: boolean;
  };
  identityVerified: boolean;
  carrier: CarrierData | null;
  vehicle: VehicleData | null;
  uwAnswers: UWAnswers;
  quote: QuoteData | null;
  photos: Record<string, string>;
  mvrResult: MVRResult | null;
  paymentComplete: boolean;
  bankEnrolled: boolean;
  policyNumber: string;
  linkExpired: boolean;
}

const initialState: PortalState = {
  currentStep: 1,
  consents: {
    tcpa: false,
    esign: false,
    privacy: false,
    cameraLocation: false,
  },
  identityVerified: false,
  carrier: null,
  vehicle: null,
  uwAnswers: {
    driverStatus: "",
    carrierName: "",
    operationScope: "",
    garagingZip: "",
    priorInsurance: "",
    losses: "",
    lossCount: "",
    cmvExperience: "",
    ticketsViolations: "",
  },
  quote: null,
  photos: {},
  mvrResult: null,
  paymentComplete: false,
  bankEnrolled: false,
  policyNumber: "",
  linkExpired: false,
};

export function usePortalStore() {
  const [state, setState] = useState<PortalState>(initialState);

  const setStep = useCallback((step: number) => {
    setState((s) => ({ ...s, currentStep: step }));
  }, []);

  const nextStep = useCallback(() => {
    setState((s) => ({ ...s, currentStep: Math.min(s.currentStep + 1, 10) }));
  }, []);

  const prevStep = useCallback(() => {
    setState((s) => ({ ...s, currentStep: Math.max(s.currentStep - 1, 1) }));
  }, []);

  const updateState = useCallback((partial: Partial<PortalState>) => {
    setState((s) => ({ ...s, ...partial }));
  }, []);

  const updateConsent = useCallback(
    (key: keyof PortalState["consents"], value: boolean) => {
      setState((s) => ({
        ...s,
        consents: { ...s.consents, [key]: value },
      }));
    },
    []
  );

  const updateUWAnswers = useCallback(
    (key: keyof UWAnswers, value: string) => {
      setState((s) => ({
        ...s,
        uwAnswers: { ...s.uwAnswers, [key]: value },
      }));
    },
    []
  );

  const updateQuote = useCallback((partial: Partial<QuoteData>) => {
    setState((s) => ({
      ...s,
      quote: s.quote ? { ...s.quote, ...partial } : null,
    }));
  }, []);

  const setPhoto = useCallback((slot: string, dataUrl: string) => {
    setState((s) => ({
      ...s,
      photos: { ...s.photos, [slot]: dataUrl },
    }));
  }, []);

  return {
    state,
    setStep,
    nextStep,
    prevStep,
    updateState,
    updateConsent,
    updateUWAnswers,
    updateQuote,
    setPhoto,
  };
}
